import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { CHANGELOG } from "../../data/changelog";
import { useUserHook } from "../../hooks/UserHook";

const LAST_SEEN_KEY = 'lastSeenChangelogVersion';
// Strategiewechsel (TASKS.md 2026-07-22): Beta-Hinweis nutzt dieselbe Dialog-Infrastruktur wie
// der Changelog - ein Abschnitt IN diesem Dialog statt eines zweiten Popups nach dem Login.
const BETA_DISMISSED_KEY = 'betaNoticeDismissed';
const isBetaNoticeDismissed = () => localStorage.getItem(BETA_DISMISSED_KEY) === 'true';
const markBetaNoticeDismissed = () => localStorage.setItem(BETA_DISMISSED_KEY, 'true');

// Leichtgewichtiger externer Store (gleiches Muster wie utils/chat/runtimeChat.tsx), damit sowohl
// der automatische "nach Login"-Check (ChangelogDialogHost) als auch der manuelle Klick auf die
// Versions-Anzeige (Sidebar.tsx) denselben Dialog steuern koennen, ohne AppContext zu erweitern.
type DialogMode = 'closed' | 'unseen' | 'all';
let dialogMode: DialogMode = 'closed';
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(listener => listener());

export const openChangelogDialog = (mode: 'unseen' | 'all' = 'all') => {
    dialogMode = mode;
    notify();
};

const closeChangelogDialog = () => {
    dialogMode = 'closed';
    notify();
};

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};
const getSnapshot = (): DialogMode => dialogMode;

// Fuer die Reihenfolge der Erst-Login-Dialoge (Beta/Changelog -> Willkommens-Tour, TourHost.tsx):
// eine Tour startet erst, wenn dieser Dialog zu ist.
export const subscribeChangelogDialog = subscribe;
export const isChangelogDialogOpen = () => dialogMode !== 'closed';

const getUnseenEntries = () => {
    const lastSeen = localStorage.getItem(LAST_SEEN_KEY) ?? '';
    return CHANGELOG.filter(entry => entry.version > lastSeen);
};

const markChangelogSeen = () => {
    if (CHANGELOG.length === 0) return;
    localStorage.setItem(LAST_SEEN_KEY, CHANGELOG[0].version);
};

const ChangelogDialog = ({ entries, showBeta, onClose }: { entries: typeof CHANGELOG; showBeta: boolean; onClose: (dismissBeta: boolean) => void }) => {
    const { t, i18n } = useTranslation(['changelog', 'common']);
    const lang = i18n.resolvedLanguage === 'de' ? 'de' : 'en';
    const latest = entries[0];
    const [dismissBeta, setDismissBeta] = useState(false);
    const close = () => onClose(dismissBeta);

    return createPortal(
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 999999 }}
            onClick={close}
        >
            <div
                className="sengi-dialog p-4 text-body"
                style={{ maxWidth: 420, width: '90%' }}
                onClick={e => e.stopPropagation()}
            >
                {showBeta && (
                    <div className="mb-4 pb-3 border-bottom">
                        <h5>{t('common:beta.noticeTitle')}</h5>
                        <p className="mb-2">{t('common:beta.noticeBody')}</p>
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="beta-dont-show-again"
                                checked={dismissBeta}
                                onChange={e => setDismissBeta(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="beta-dont-show-again">
                                {t('common:beta.dontShowAgain')}
                            </label>
                        </div>
                    </div>
                )}
                {latest && (
                    <>
                        <h5>{t('title', { version: latest.version })}</h5>
                        <div className="mb-4">
                            {entries.map(entry => (
                                <div key={entry.version} className="mb-3">
                                    {entries.length > 1 && (
                                        <div className="small text-secondary mb-1">{entry.version} · {entry.date}</div>
                                    )}
                                    <ul className="mb-0 ps-3">
                                        {entry.entries[lang].map((line, index) => (
                                            <li key={index}>{line}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-primary" onClick={close}>
                        {t('close')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// In PageMasterTemplate gemountet (App-Shell, laeuft fuer jede Route mit). Prueft nach dem Login
// einmalig auf ungesehene Changelog-Eintraege UND den noch nicht bestaetigten Beta-Hinweis
// (Strategiewechsel, TASKS.md 2026-07-22: "nicht zwei Popups nacheinander" - beides landet noetigenfalls
// im selben Dialog) und haelt gleichzeitig den manuell ueber die Versions-Anzeige (Sidebar.tsx,
// openChangelogDialog('all')) geoeffneten Dialog bereit.
export const ChangelogDialogHost = () => {
    const [user] = useUserHook();
    const mode = useSyncExternalStore(subscribe, getSnapshot);

    useEffect(() => {
        if (!user) return;
        if (getUnseenEntries().length > 0 || !isBetaNoticeDismissed()) {
            openChangelogDialog('unseen');
        }
    }, [user]);

    if (mode === 'closed') return null;

    const entries = mode === 'unseen' ? getUnseenEntries() : CHANGELOG;
    const showBeta = !isBetaNoticeDismissed();
    if (entries.length === 0 && !showBeta) return null;

    const handleClose = (dismissBeta: boolean) => {
        markChangelogSeen();
        if (dismissBeta) markBetaNoticeDismissed();
        closeChangelogDialog();
    };

    return <ChangelogDialog entries={entries} showBeta={showBeta} onClose={handleClose} />;
};
